/**
 * Page-local artifact-panel state (ADR 0007 — no store). Holds the open artifact, docked/expanded mode, and the
 * element that opened it so focus can return there on close. The artifact is scoped to the thread it was opened
 * on: switching threads (the panel lives outside ChatBox's navKey remount) derives it closed, since the opening
 * card belongs to the previous thread. "New chat" derives it closed too, whatever it is scoped to.
 * `ArtifactPanelContext` exposes just `openArtifact` to the inline cards,
 * which render deep inside `<ChatBox>` and can't be handed a callback prop directly.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { useLatest } from "./use-latest"
import { type ArtifactEnvelope, sharedArtifactResourceId } from "./artifact-contract"

/** A ref whose `current` we only read + `focus()` — `readonly` so a `RefObject<HTMLDivElement | null>` fits. */
interface FocusFallbackRef {
  readonly current: HTMLElement | null
}

/**
 * How the panel came to be showing this artifact — a click on a card, a shared artifact's URL, or a
 * thread switch re-deriving one that was never closed.
 */
export type ArtifactViewSource = "card" | "deep-link" | "restored"

export interface ArtifactPanelContextValue {
  openArtifact: (artifact: ArtifactEnvelope, opener: HTMLElement | null, name?: string) => void
  activeThreadId: string | null
}

const ArtifactPanelContext = createContext<ArtifactPanelContextValue | null>(null)
export const ArtifactPanelProvider = ArtifactPanelContext.Provider

/** For the inline card. Returns null when rendered outside a panel region (the card's Open then no-ops). */
export function useArtifactPanelActions(): ArtifactPanelContextValue | null {
  return useContext(ArtifactPanelContext)
}

export interface UseArtifactPanelResult {
  /** Opens straight into full screen, for a link whose whole purpose was this artifact (AI-734). */
  openArtifactExpanded: (artifact: ArtifactEnvelope, name?: string) => void
  artifact: ArtifactEnvelope | null
  artifactName: string | null
  isExpanded: boolean
  close: () => void
  toggleExpand: () => void
  /** Esc: collapse an expanded panel back to docked first, else close. */
  handleEscape: () => void
  contextValue: ArtifactPanelContextValue
}

interface OpenArtifactState {
  artifact: ArtifactEnvelope
  threadId: string | null
  newChatSeq: number
  name?: string
  /** Set by openArtifact; a view that comes back via a thread switch reports `restored` instead. */
  source: ArtifactViewSource
}

/**
 * @param activeThreadId  the conversation the panel is scoped to; a change derives the panel closed
 * @param fallbackFocusRef focus target when the opening card is gone on close (e.g. after external deletion)
 * @param newChatSeq       "New chat" counter; a change derives the panel closed whatever it is scoped to
 */
export function useArtifactPanel(activeThreadId: string | null, fallbackFocusRef: FocusFallbackRef, newChatSeq = 0): UseArtifactPanelResult {
  const [openState, setOpenState] = useState<OpenArtifactState | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const openerRef = useRef<HTMLElement | null>(null)
  const analytics = useAnalytics()

  const openArtifact = useCallback((artifact: ArtifactEnvelope, opener: HTMLElement | null, name?: string) => {
    openerRef.current = opener
    setIsExpanded(false)
    // An artifact addressed by resource belongs to no thread — stamping the selected one would derive it
    // closed on the next thread switch and report that unrelated id in its view/close/download events.
    const threadId = sharedArtifactResourceId(artifact) != null ? null : activeThreadId
    setOpenState({ artifact, threadId, newChatSeq, source: "card", ...(name !== undefined && { name }) })
  }, [activeThreadId, newChatSeq])

  // No opener to return focus to — the user arrived from outside the app — so close falls back to the
  // chat column, which is what `fallbackFocusRef` is for.
  const openArtifactExpanded = useCallback((artifact: ArtifactEnvelope, name?: string) => {
    openerRef.current = null
    setIsExpanded(true)
    // Same rule as openArtifact: a deep-linked artifact is addressed by resource and belongs to no
    // conversation, so the selected one must not reach its view, close and fullscreen events.
    const threadId = sharedArtifactResourceId(artifact) != null ? null : activeThreadId
    setOpenState({ artifact, threadId, newChatSeq, source: "deep-link", ...(name !== undefined && { name }) })
  }, [activeThreadId, newChatSeq])

  const close = useCallback(() => {
    setOpenState(null)
    setIsExpanded(false)
    // Return focus to the opening card if it's still in the document; otherwise to a stable fallback so it
    // never drops to <body>.
    const opener = openerRef.current
    openerRef.current = null
    if (opener?.isConnected) opener.focus()
    else fallbackFocusRef.current?.focus()
  }, [fallbackFocusRef])

  const toggleExpand = useCallback(() => {
    setIsExpanded((expanded) => !expanded)
    if (openState == null) return
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_FULLSCREEN_TOGGLED, {
      expanded: !isExpanded,
      artifactId: openState.artifact.id,
      artifactType: openState.artifact.type,
      ...(openState.threadId != null && { threadId: openState.threadId }),
    })
  }, [openState, isExpanded, analytics])

  const handleEscape = useCallback(() => {
    if (isExpanded) toggleExpand()
    else close()
  }, [isExpanded, toggleExpand, close])

  const contextValue = useMemo<ArtifactPanelContextValue>(() => ({ openArtifact, activeThreadId }), [openArtifact, activeThreadId])

  // A panel opened on a brand-new chat (activeThreadId null) belongs to the conversation that adopts a server id
  // on the first turn — that adoption is not a thread switch. Commit the adopted id during render (React-sanctioned)
  // so the panel survives adoption yet a later real switch still derives it closed.
  const isResourceAddressed = openState != null && sharedArtifactResourceId(openState.artifact) != null
  if (openState != null && !isResourceAddressed && openState.threadId === null && activeThreadId != null) {
    setOpenState({ ...openState, threadId: activeThreadId })
  }

  // Derived — the panel belongs to the thread it was opened on (see file header). No effect / no setState needed.
  // A resource-addressed artifact has no thread, so no switch derives it closed — only "New chat" does.
  const isCurrent = openState != null && openState.newChatSeq === newChatSeq
    && (isResourceAddressed || openState.threadId === activeThreadId)
  const artifact = isCurrent ? openState.artifact : null
  const artifactName = isCurrent ? openState.name ?? null : null

  // Both events come off the derived artifact rather than open()/close(): a thread switch, or a card opening
  // over the current artifact, changes what is on screen without either function running. Opening is a view
  // wherever it comes from — including switching back to a thread, which re-shows the artifact that was
  // never cleared, only derived away.
  const openArtifactState = isCurrent ? openState : null
  const prevOpenArtifactRef = useRef(openArtifactState)
  // The open-state OBJECT already reported as a click, not its id. Leaving a thread derives the panel closed and
  // returning re-derives the same object, which is the restore this distinguishes; every genuine open (a reclick
  // after close, or re-opening A after B) goes through `openArtifact` and is a fresh object, so it still counts
  // as a click. Written in the effect — refs are write-only during render here (react-hooks/refs).
  const clickReportedStateRef = useRef<OpenArtifactState | null>(null)
  useEffect(() => {
    const previous = prevOpenArtifactRef.current
    prevOpenArtifactRef.current = openArtifactState
    if (previous?.artifact.id === openArtifactState?.artifact.id) {
      // Adopting a server thread id replaces the open-state object with a spread copy while the artifact stays
      // the same. Carry the click report onto the new object, or a later there-and-back would fail the identity
      // check below and report a restore as a fresh click.
      if (clickReportedStateRef.current === previous) clickReportedStateRef.current = openArtifactState
      return
    }
    if (previous != null) {
      analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_CLOSED, {
        artifactId: previous.artifact.id,
        artifactType: previous.artifact.type,
        ...(previous.threadId != null && { threadId: previous.threadId }),
      })
    }
    if (openArtifactState != null) {
      const reportedSource: ArtifactViewSource = openArtifactState.source !== "restored" && clickReportedStateRef.current !== openArtifactState
        ? openArtifactState.source
        : "restored"
      clickReportedStateRef.current = openArtifactState
      analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_VIEWED, {
        artifactId: openArtifactState.artifact.id,
        artifactType: openArtifactState.artifact.type,
        // A view the user asked for and one that reappeared under them are different facts; without this they
        // were one number, and switching threads back and forth inflated it.
        source: reportedSource,
        ...(openArtifactState.threadId != null && { threadId: openArtifactState.threadId }),
      })
    }
  }, [openArtifactState, analytics])

  // Teardown with an artifact still open is a close the transition above never sees — the surface goes away
  // (leaving /kelly-ai, an account switch, a boundary catch) without the derived artifact ever going null.
  // Analytics through a ref so this stays an unmount-only effect rather than re-arming on every render.
  const analyticsRef = useLatest(analytics)
  useEffect(() => () => {
    const previous = prevOpenArtifactRef.current
    if (previous == null) return
    analyticsRef.current.track(AI_TEAMMATE_EVENTS.ARTIFACT_CLOSED, {
      artifactId: previous.artifact.id,
      artifactType: previous.artifact.type,
      ...(previous.threadId != null && { threadId: previous.threadId }),
    })
  }, [analyticsRef])

  return { artifact, artifactName, isExpanded, close, toggleExpand, handleEscape, contextValue, openArtifactExpanded }
}
